import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, DeleteSweep, Download, PlayArrow, PlaylistPlay, WarningAmber } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { directShearColumns } from './DirectShearColumns';
import { addReading, updateReading, deleteReading, bulkDeleteReadings, calculateStage as calculateStageApi } from '../../../../services/directShearReadingService';
import type { DirectShearReading } from '../../../../types/directShearReading';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useTestFormulas } from '../../../../hooks/useTestFormulas';
import { useRowRangeSelection } from '../../../../hooks/useRowRangeSelection';
import { ROW_RANGE_SELECT_TIP, SELECT_ALL_ROWS_TOOLTIP } from '../../../../constants/rowSelection';
import { FormulaHeaderTooltip } from '../../components/FormulaHeaderTooltip';

interface DirectShearDataGridProps {
  generalKey: string;
  readings: DirectShearReading[];
  activeStage: number;
  onStageChange: (stage: number) => void;
  stageCounts: Record<number, number>;
  loading: boolean;
  onDataChange: () => void;
  savedDilationRange?: number | null;
  /** Per-stage dilation windows keyed by stage number (BUG-2026-0211); null = never calculated. */
  stageDilationRanges?: Record<number, number | null>;
  /** Page-level lock (`!canEditTestData` from TestWorkflowPage); hides every edit affordance. */
  readOnly?: boolean;
}

const VISIBLE_ROWS = 50;
const ROW_HEIGHT = 32;
const COL_COUNT = directShearColumns.length + 2; // +checkbox +delete

export const DirectShearDataGrid = ({
  generalKey, readings, activeStage, onStageChange, stageCounts, loading, onDataChange, savedDilationRange,
  stageDilationRanges, readOnly,
}: DirectShearDataGridProps) => {
  const theme = useTheme();
  const { canEditTestData } = usePermissions();
  const canEdit = canEditTestData && !readOnly;
  const { formulaFor } = useTestFormulas('directShear');
  const [editCell, setEditCell] = useState<{ row: number; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const [sortField, setSortField] = useState<string>('SampleInt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // Row selection (single toggle, select-all, Shift-click range) — shared hook,
  // BUG-2026-0188. Rows are addressed by DirectShearReadingKey; the selection
  // is scoped to the active stage (cleared on every stage switch below).
  const sel = useRowRangeSelection();
  const [calculating, setCalculating] = useState(false);
  const [calcProgress, setCalcProgress] = useState<string | null>(null);
  const [deleteProgress, setDeleteProgress] = useState<string | null>(null);
  // Dilation window is PER STAGE (BUG-2026-0211): seed from the active stage's
  // stored value, then the test-level value, then the 0.375 mm spec default —
  // and re-seed whenever the stage changes or its stored value is refreshed
  // (e.g. Analysis & Plots recalculated it).
  const stageDilation = stageDilationRanges?.[activeStage] ?? savedDilationRange ?? 0.375;
  const [dilationRange, setDilationRange] = useState(() => stageDilation);
  useEffect(() => { setDilationRange(stageDilation); }, [activeStage, stageDilation]);
  const [displayDecimals, setDisplayDecimals] = useState(4);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({
    open: false, title: '', message: '', onConfirm: () => {},
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' | 'info' }>({ open: false, msg: '', sev: 'success' });

  useEffect(() => { setScrollTop(0); setSortField('SampleInt'); setSortDir('asc'); sel.clear(); if (containerRef.current) containerRef.current.scrollTop = 0; }, [activeStage, sel.clear]);

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sortedReadings = useMemo(() => {
    return [...readings].sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[sortField] as number ?? 0;
      const bv = (b as unknown as Record<string, unknown>)[sortField] as number ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [readings, sortField, sortDir]);

  const startIdx = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - 5);
  const endIdx = Math.min(sortedReadings.length, startIdx + VISIBLE_ROWS + 10);
  const visibleRows = sortedReadings.slice(startIdx, endIdx);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => { setScrollTop(e.currentTarget.scrollTop); }, []);

  // Stable row ids in DISPLAY order (current sort), across the whole stage —
  // not just the virtualized window — so a Shift-click range can span rows
  // that are scrolled out of view. `readings` is already the active stage only.
  const orderedIds = useMemo(() => sortedReadings.map((r) => r.DirectShearReadingKey), [sortedReadings]);
  const allSelected = orderedIds.length > 0 && sel.selected.size === orderedIds.length;
  const someSelected = sel.selected.size > 0 && sel.selected.size < orderedIds.length;

  const handleCellClick = (absIdx: number, field: string, value: unknown) => {
    if (!canEdit) return;
    const col = directShearColumns.find((c) => c.field === field);
    if (!col || !col.editable) return;
    setEditCell({ row: absIdx, field });
    setEditValue(value != null ? String(value) : '');
  };

  const handleCellBlur = async () => {
    if (!editCell) return;
    const reading = sortedReadings[editCell.row];
    if (!reading) { setEditCell(null); return; }
    const col = directShearColumns.find((c) => c.field === editCell.field);
    if (!col?.dbField) { setEditCell(null); return; }
    const numVal = editValue === '' ? null : Number(editValue);
    const currentVal = (reading as unknown as Record<string, unknown>)[editCell.field];
    if (numVal === currentVal || (numVal === null && currentVal === null)) { setEditCell(null); return; }
    // Validate SampleInt uniqueness
    if (editCell.field === 'SampleInt' && numVal != null) {
      const duplicate = readings.find((r) =>
        r.DirectShearReadingKey !== reading.DirectShearReadingKey && r.SampleInt === numVal,
      );
      if (duplicate) {
        setSnack({ open: true, msg: `Duplicate interval number ${numVal}. Please enter a unique value.`, sev: 'error' });
        setEditCell(null);
        return;
      }
    }
    try {
      await updateReading(reading.DirectShearReadingKey, { [col.dbField]: numVal } as Partial<DirectShearReading>);
      onDataChange();
    } catch { setSnack({ open: true, msg: 'Failed to save', sev: 'error' }); }
    setEditCell(null);
  };

  const handleAddRow = async () => {
    // Auto-assign next SampleInt = max existing + 1
    const maxInt = readings.reduce((max, r) => Math.max(max, r.SampleInt ?? 0), 0);
    try {
      await addReading(generalKey, activeStage, { SampleInt: maxInt + 1 });
      onDataChange();
    } catch { setSnack({ open: true, msg: 'Failed to add row', sev: 'error' }); }
  };

  const handleDeleteRow = async (reading: DirectShearReading) => {
    try { await deleteReading(reading.DirectShearReadingKey); sel.removeId(reading.DirectShearReadingKey); onDataChange(); }
    catch { setSnack({ open: true, msg: 'Failed to delete', sev: 'error' }); }
  };

  const showConfirm = (title: string, message: string, onConfirm: () => void) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const handleBulkDelete = () => {
    if (sel.selected.size === 0) return;
    const count = sel.selected.size;
    showConfirm('Delete Selected Rows?', `This will remove ${count} row${count > 1 ? 's' : ''} from Stage ${activeStage}.`, async () => {
      const keys = Array.from(sel.selected);
      try {
        setDeleteProgress(`Deleting ${count} rows...`);
        const result = await bulkDeleteReadings(keys);
        sel.clear();
        setDeleteProgress(null);
        onDataChange();
        setSnack({ open: true, msg: `${result.deleted} rows deleted`, sev: 'success' });
      } catch {
        setDeleteProgress(null);
        setSnack({ open: true, msg: 'Failed to delete rows', sev: 'error' });
      }
    });
  };

  const runCalculation = async (stage: number): Promise<number> => {
    setCalculating(true);
    const result = await calculateStageApi(generalKey, stage, dilationRange);
    return result.rowsCalculated;
  };

  const handleCalculate = () => {
    const hasCalcData = readings.some((r) => r.IncrementalHorizontalDisplacement != null);
    const doCalc = async () => {
      try {
        setCalcProgress(`Calculating Stage ${activeStage} (${readings.length} rows)...`);
        const rows = await runCalculation(activeStage);
        setCalcProgress(null);
        onDataChange();
        setCalculating(false);
        setSnack({ open: true, msg: `Stage ${activeStage}: calculated ${rows} rows`, sev: 'success' });
      } catch (err: unknown) {
        setCalcProgress(null);
        setCalculating(false);
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        setSnack({ open: true, msg: msg || `Stage ${activeStage}: calculation failed`, sev: 'error' });
      }
    };
    if (hasCalcData) {
      showConfirm('Recalculate Stage?', `Stage ${activeStage} already has calculated data. This will overwrite all computed values.`, doCalc);
    } else { doCalc(); }
  };

  const handleCalculateAll = () => {
    const stagesWithData = [1, 2, 3, 4, 5].filter((s) => stageCounts[s] > 0);
    if (stagesWithData.length === 0) { setSnack({ open: true, msg: 'No stages have data to calculate', sev: 'error' }); return; }
    showConfirm(
      'Calculate All Stages?',
      `This will run calculations on ${stagesWithData.length} stage${stagesWithData.length > 1 ? 's' : ''} (${stagesWithData.map(s => 'Stage ' + s).join(', ')}). Existing calculated values will be overwritten.`,
      async () => {
        const summaries: string[] = [];
        for (let i = 0; i < stagesWithData.length; i++) {
          const s = stagesWithData[i];
          setCalcProgress(`[${i + 1}/${stagesWithData.length}] Calculating Stage ${s} (${stageCounts[s]} rows)...`);
          try {
            const rows = await runCalculation(s);
            summaries.push(`S${s}: ${rows}`);
          } catch (err: unknown) {
            const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
            summaries.push(`S${s}: failed`);
            setSnack({ open: true, msg: msg || `Stage ${s}: calculation failed`, sev: 'error' });
          }
        }
        setCalcProgress(null);
        onDataChange();
        setCalculating(false);
        setSnack({ open: true, msg: `Done — ${summaries.join(' | ')}`, sev: 'success' });
      },
    );
  };

  const handleExport = () => {
    const headers = directShearColumns.map((c) => c.headerName).join(',');
    const rows = sortedReadings.map((r) =>
      directShearColumns.map((c) => (r as unknown as Record<string, unknown>)[c.field] ?? '').join(','),
    );
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `direct-shear-stage${activeStage}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const inputBg = alpha('#d4edda', 0.15);
  const calcBg = alpha('#fff3cd', 0.1);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* Stage Tabs + floating selection toolbar */}
      <Box sx={{ position: 'relative', borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex' }}>
          {[1, 2, 3, 4, 5].map((s) => (
            <Box key={s} onClick={() => onStageChange(s)}
              sx={{ px: 2.5, py: 1, cursor: 'pointer', fontWeight: activeStage === s ? 700 : 500,
                fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 0.75,
                borderBottom: activeStage === s ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                color: activeStage === s ? theme.palette.primary.main : 'text.secondary',
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}>
              Stage {s}
              {stageCounts[s] > 0 && (
                <Chip label={stageCounts[s]} size="small"
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, backgroundColor: alpha(theme.palette.success.main, 0.15) }} />
              )}
            </Box>
          ))}
        </Box>
        {/* Right-side toolbar — selection actions or Calculate button */}
        <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', gap: 1.5, px: 2 }}>
          {deleteProgress ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: 1.5,
              backgroundColor: alpha(theme.palette.warning.light, 0.12) }}>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem', color: 'warning.dark' }}>
                {deleteProgress}
              </Typography>
            </Box>
          ) : sel.selected.size > 0 && canEdit ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: 1.5,
              backgroundColor: alpha(theme.palette.error.light, 0.08) }}>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem', color: 'error.dark' }}>
                {sel.selected.size} selected
              </Typography>
              <Button size="small" color="error" variant="contained" startIcon={<DeleteSweep sx={{ fontSize: 14 }} />}
                onClick={handleBulkDelete} sx={{ textTransform: 'none', fontSize: '0.72rem', borderRadius: 1.5, py: 0.25, px: 1.5 }}>
                Delete selected ({sel.selected.size})
              </Button>
              <Button size="small" onClick={() => sel.clear()}
                sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'text.secondary', minWidth: 0 }}>
                Clear
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {calcProgress && (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'primary.main', mr: 1 }}>
                  {calcProgress}
                </Typography>
              )}
              {canEdit &&readings.length > 0 && (
                <Button size="small" variant="contained" startIcon={<PlayArrow sx={{ fontSize: 18 }} />}
                  onClick={handleCalculate} disabled={calculating}
                  sx={{
                    textTransform: 'none', fontSize: '0.82rem', py: 0.7, px: 2.5, borderRadius: 2, fontWeight: 700,
                    background: 'linear-gradient(135deg, #4A6741 0%, #5c7f52 100%)',
                    boxShadow: '0 2px 8px rgba(74,103,65,0.3)',
                    '&:hover': { background: 'linear-gradient(135deg, #3d5636 0%, #4A6741 100%)', boxShadow: '0 3px 12px rgba(74,103,65,0.4)' },
                  }}>
                  {calculating && !calcProgress ? 'Processing...' : `Process Stage ${activeStage}`}
                </Button>
              )}
              {canEdit &&Object.values(stageCounts).some((c) => c > 0) && (
                <Button size="small" variant="contained" startIcon={<PlaylistPlay sx={{ fontSize: 20 }} />}
                  onClick={handleCalculateAll} disabled={calculating}
                  sx={{
                    textTransform: 'none', fontSize: '0.82rem', py: 0.7, px: 2.5, borderRadius: 2, fontWeight: 700,
                    background: 'linear-gradient(135deg, #1a3a6b 0%, #2a5298 100%)',
                    boxShadow: '0 2px 8px rgba(26,58,107,0.3)',
                    '&:hover': { background: 'linear-gradient(135deg, #142d54 0%, #1a3a6b 100%)', boxShadow: '0 3px 12px rgba(26,58,107,0.4)' },
                  }}>
                  Process All Stages
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Data Grid */}
      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><Typography color="text.secondary">Loading...</Typography></Box>
      ) : readings.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">No data for Stage {activeStage}. Upload a .rmcplots or Excel (.xlsx) file, or add rows manually.</Typography>
        </Box>
      ) : (
        <TableContainer ref={containerRef} onScroll={handleScroll} sx={{ maxHeight: 500, overflow: 'auto' }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell colSpan={2} sx={{ backgroundColor: '#e8f5e9', zIndex: 3, width: 64, borderBottom: `2px solid ${theme.palette.divider}`, p: 0.5, borderRight: 'none' }}>
                  <Tooltip title={SELECT_ALL_ROWS_TOOLTIP}>
                    <Checkbox size="small" checked={allSelected} indeterminate={someSelected} disabled={!canEdit}
                      onChange={() => sel.toggleSelectAll(orderedIds)} />
                  </Tooltip>
                </TableCell>
                {directShearColumns.map((col) => {
                  const reg = formulaFor(col.columnKey);
                  return (
                  <FormulaHeaderTooltip key={col.field} label={col.headerName} formula={reg?.formula} note={reg?.note}>
                    <TableCell onClick={() => handleSort(col.field)}
                      sx={{ width: col.width, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        backgroundColor: col.source === 'input' ? '#e8f5e9' : col.source === 'calculated' ? '#fff8e1' : theme.palette.background.paper,
                        borderBottom: `2px solid ${theme.palette.divider}`, px: 0.5, py: 0.5,
                        cursor: 'pointer', userSelect: 'none', zIndex: 3 }}>
                      {col.shortName}
                      {sortField === col.field && <span style={{ marginLeft: 2 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>}
                    </TableCell>
                  </FormulaHeaderTooltip>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {startIdx > 0 && <TableRow><TableCell colSpan={COL_COUNT} sx={{ height: startIdx * ROW_HEIGHT, p: 0, border: 0 }} /></TableRow>}
              {visibleRows.map((reading, vIdx) => {
                const absIdx = startIdx + vIdx;
                const isSelected = sel.isSelected(reading.DirectShearReadingKey);
                return (
                  <TableRow key={reading.DirectShearReadingKey} sx={{ height: ROW_HEIGHT,
                    backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.06) : undefined }}>
                    <TableCell padding="checkbox" sx={{ width: 36, p: 0.5 }}>
                      <Checkbox size="small" disabled={!canEdit} {...sel.getRowCheckboxProps(orderedIds, absIdx)} />
                    </TableCell>
                    <TableCell sx={{ p: 0, width: 28 }}>
                      {canEdit &&(
                        <IconButton size="small" onClick={() => handleDeleteRow(reading)}
                          sx={{ color: 'error.main', opacity: 0.2, '&:hover': { opacity: 1 } }}>
                          <Delete sx={{ fontSize: 12 }} />
                        </IconButton>
                      )}
                    </TableCell>
                    {directShearColumns.map((col) => {
                      const val = (reading as unknown as Record<string, unknown>)[col.field];
                      const isEditing = editCell?.row === absIdx && editCell?.field === col.field;
                      const bg = col.source === 'input' ? inputBg : col.source === 'calculated' ? calcBg : undefined;
                      return (
                        <TableCell key={col.field} onClick={() => handleCellClick(absIdx, col.field, val)}
                          sx={{ px: 0.5, py: 0, fontSize: '0.7rem', fontFamily: 'monospace',
                            backgroundColor: bg, cursor: col.editable ? 'text' : 'default',
                            width: col.width, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {isEditing ? (
                            <TextField size="small" value={editValue} autoFocus
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={handleCellBlur}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleCellBlur(); if (e.key === 'Escape') setEditCell(null); }}
                              sx={{ '& input': { fontSize: '0.7rem', fontFamily: 'monospace', p: '2px 4px' } }}
                              fullWidth variant="standard" />
                          ) : (
                            val != null && val !== '' ? (typeof val === 'number' ? val.toFixed(col.decimalPlaces === 0 ? 0 : displayDecimals) : String(val)) : (
                              col.source === 'calculated' ? <span style={{ color: alpha('#000', 0.2) }}>—</span> : ''
                            )
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {endIdx < sortedReadings.length && (
                <TableRow><TableCell colSpan={COL_COUNT} sx={{ height: (sortedReadings.length - endIdx) * ROW_HEIGHT, p: 0, border: 0 }} /></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Footer */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            {readings.length > 0 ? `${readings.length} rows` : 'No data'}
          </Typography>
          {canEdit && readings.length > 0 && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
              {ROW_RANGE_SELECT_TIP}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Decimals:</Typography>
            <TextField size="small" type="number" value={displayDecimals}
              onChange={(e) => setDisplayDecimals(Math.max(0, Math.min(8, Number(e.target.value) || 4)))}
              inputProps={{ min: 0, max: 8 }}
              sx={{ width: 45, '& input': { fontSize: '0.7rem', p: '2px 4px', textAlign: 'center' } }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={`Dilation averaging window for Stage ${activeStage} only — each stage keeps its own value (BUG-2026-0211).`}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', cursor: 'help' }}>
                Dilation Range (Stage {activeStage}):
              </Typography>
            </Tooltip>
            <TextField size="small" type="number" value={dilationRange}
              onChange={(e) => setDilationRange(Math.max(0.01, Number(e.target.value) || 0.375))}
              inputProps={{ step: 0.025, min: 0.01, max: 5 }}
              sx={{ width: 55, '& input': { fontSize: '0.7rem', p: '2px 4px', textAlign: 'center' } }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>mm</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit &&(
            <Button size="small" startIcon={<Add sx={{ fontSize: 14 }} />} onClick={handleAddRow}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Add Row</Button>
          )}
          {readings.length > 0 && (
            <Button size="small" startIcon={<Download sx={{ fontSize: 14 }} />} onClick={handleExport}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Export CSV</Button>
          )}
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog((p) => ({ ...p, open: false }))}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, pb: 0 }}>
          <WarningAmber sx={{ color: 'warning.main' }} /> {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDialog((p) => ({ ...p, open: false }))} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => { setConfirmDialog((p) => ({ ...p, open: false })); confirmDialog.onConfirm(); }}
            sx={{ textTransform: 'none', fontWeight: 600 }}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={2000} onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Paper>
  );
};
